/**
 * Mira LINE 收件匣 — 渠道分類 + Intent Tag + 對話狀態管理
 * 規格參考：AI 客服收件匣功能規格書 v1.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layout, Button, Input, Tag, Space, Typography, Avatar,
  message, Spin, Empty, Badge, Tooltip, Select, Segmented,
  Drawer, Form, Modal, Popconfirm, List,
} from 'antd';
import {
  SendOutlined, ReloadOutlined, RobotOutlined, UserOutlined,
  MessageOutlined, BulbOutlined, EditOutlined, SettingOutlined,
  PlusOutlined, DeleteOutlined, CheckCircleOutlined,
} from '@ant-design/icons';

const { Sider, Content } = Layout;
const { Text, Title } = Typography;
const { TextArea } = Input;

const SPO = '/api/v1';
const spoH = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

// ── Status config ─────────────────────────────────────────────────
const STATUS = {
  pending:     { label: '待處理', color: 'orange' },
  in_progress: { label: '處理中', color: 'blue' },
  done:        { label: '已處理', color: 'green' },
};

// ── Channel config ────────────────────────────────────────────────
const CHANNEL_ICON = {
  line: <span style={{ background: '#06c755', color: '#fff', borderRadius: 3, padding: '0 4px', fontSize: 10, fontWeight: 700 }}>LINE</span>,
  fb:   <span style={{ background: '#1877f2', color: '#fff', borderRadius: 3, padding: '0 4px', fontSize: 10, fontWeight: 700 }}>FB</span>,
};

// ── Proxy LINE API via SPO backend ────────────────────────────────
async function lineGet(path) {
  const r = await fetch(`${SPO}/line/openclaw${path}`, { headers: spoH() });
  return r.json();
}
async function linePost(path, body) {
  const r = await fetch(`${SPO}/line/openclaw${path}`, { method: 'POST', headers: spoH(), body: JSON.stringify(body) });
  return r.json();
}

// ── Inbox API helpers ─────────────────────────────────────────────
async function inboxGet(path) {
  const r = await fetch(`${SPO}/inbox${path}`, { headers: spoH() });
  return r.json();
}
async function inboxPost(path, body) {
  const r = await fetch(`${SPO}/inbox${path}`, { method: 'POST', headers: spoH(), body: JSON.stringify(body) });
  return r.json();
}
async function inboxPatch(path, body) {
  const r = await fetch(`${SPO}/inbox${path}`, { method: 'PATCH', headers: spoH(), body: JSON.stringify(body) });
  return r.json();
}

const QUICK_REPLIES = [
  '您好！感謝您的詢問，請問有什麼可以為您服務的嗎？😊',
  '我們目前有 NetAI AI 網路監控方案，舊客戶續約即可免費升級 Standard 守護版，請問您有興趣了解更多嗎？',
  '您好，我們可以為您安排一個 30 分鐘的免費顧問諮詢，方便的話請問什麼時間合適？',
  '感謝您的回覆！我們會盡快安排專人與您聯繫。如有緊急需求請撥 02-XXXX-XXXX。',
];

// ── Tag Management Drawer ─────────────────────────────────────────
function TagManagerDrawer({ open, onClose, tags, onTagsChange }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleAdd = async (values) => {
    setSaving(true);
    try {
      const res = await fetch(`${SPO}/inbox/tags`, {
        method: 'POST', headers: spoH(),
        body: JSON.stringify({ name: values.name, color: values.color || 'blue' }),
      });
      const tag = await res.json();
      onTagsChange([...tags, tag]);
      form.resetFields();
      message.success('標籤已新增');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await fetch(`${SPO}/inbox/tags/${id}`, { method: 'DELETE', headers: spoH() });
    onTagsChange(tags.filter(t => t.id !== id));
    message.success('已刪除');
  };

  const TAG_COLORS = ['blue','green','orange','red','purple','cyan','gold','volcano','geekblue','magenta'];

  return (
    <Drawer title={<><SettingOutlined /> 意圖標籤管理</>} open={open} onClose={onClose} width={380}>
      <Form form={form} layout="inline" onFinish={handleAdd} style={{ marginBottom: 16 }}>
        <Form.Item name="name" rules={[{ required: true }]}>
          <Input placeholder="標籤名稱" style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="color" initialValue="blue">
          <Select style={{ width: 90 }}>
            {TAG_COLORS.map(c => (
              <Select.Option key={c} value={c}><Tag color={c}>{c}</Tag></Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />}>新增</Button>
      </Form>
      <List
        dataSource={tags}
        renderItem={tag => (
          <List.Item actions={[
            <Popconfirm title="刪除此標籤？" onConfirm={() => handleDelete(tag.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ]}>
            <Tag color={tag.color} style={{ fontSize: 13 }}>{tag.name}</Tag>
          </List.Item>
        )}
      />
    </Drawer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export default function UlinkLineInboxPage() {
  const [contacts, setContacts] = useState([]);      // merged: LINE API + inbox DB
  const [inboxMap, setInboxMap] = useState({});      // external_id → inbox contact record
  const [tags, setTags] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [channelTab, setChannelTab] = useState('all');
  const [tagDrawer, setTagDrawer] = useState(false);
  const [nameEditModal, setNameEditModal] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const chatEndRef = useRef(null);

  // Load tags
  useEffect(() => {
    inboxGet('/tags').then(list => setTags(Array.isArray(list) ? list : []));
  }, []);

  // Fetch LINE messages + merge with inbox DB records
  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [contactsRes, historyRes, inboxContacts] = await Promise.all([
        lineGet('/contacts?limit=500'),
        lineGet('/history?limit=500'),
        inboxGet('/contacts?channel=line'),
      ]);
      const rawContacts = contactsRes.contacts || [];
      const rawMsgs = historyRes.messages || [];

      // Build inbox map: external_id → inbox record
      const imap = {};
      (Array.isArray(inboxContacts) ? inboxContacts : []).forEach(c => {
        imap[c.external_id] = c;
      });
      setInboxMap(imap);

      // Group messages by user
      const userMap = {};
      rawMsgs.forEach(m => {
        const uid = m.direction === 'inbound' ? (m.user_id || '') : (m.reply_to || '');
        if (!uid || uid.startsWith('bot:') || uid.startsWith('api:')) return;
        if (!userMap[uid]) {
          userMap[uid] = { id: uid, channel: 'line', messages: [], unread: 0, lastAt: '', lastText: '' };
        }
        userMap[uid].messages.push(m);
        if (m.created_at > userMap[uid].lastAt) {
          userMap[uid].lastAt = m.created_at;
          userMap[uid].lastText = m.direction === 'inbound' ? m.text : '(已回覆)';
        }
        if (m.direction === 'inbound') userMap[uid].unread++;
      });

      rawContacts.forEach(c => {
        if (userMap[c.id]) {
          userMap[c.id].source_type = c.source_type;
        } else {
          userMap[c.id] = { id: c.id, channel: 'line', messages: [], unread: 0, lastAt: c.last_at || '', lastText: '(尚無訊息)', source_type: c.source_type };
        }
      });

      const sorted = Object.values(userMap).sort((a, b) => b.lastAt.localeCompare(a.lastAt));
      setContacts(sorted);

      if (selectedUser) {
        const updated = sorted.find(u => u.id === selectedUser.id);
        if (updated) setSelectedUser(updated);
      }
    } catch (e) {
      message.error('載入失敗：' + e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedUser?.id]); // eslint-disable-line

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  // Auto-poll every 10s
  useEffect(() => {
    const t = setInterval(fetchData, 10000);
    return () => clearInterval(t);
  }, [fetchData]);

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedUser?.messages]);

  // Ensure inbox record exists when selecting a user
  const selectUser = async (user) => {
    setSelectedUser(user);
    setReplyText('');
    if (!inboxMap[user.id]) {
      try {
        const rec = await inboxPost('/contacts', { channel: 'line', external_id: user.id });
        setInboxMap(prev => ({ ...prev, [user.id]: rec }));
      } catch {}
    }
  };

  const inboxRec = selectedUser ? inboxMap[selectedUser.id] : null;

  const updateInboxRec = async (patch) => {
    if (!inboxRec) return;
    try {
      const updated = await inboxPatch(`/contacts/${inboxRec.id}`, patch);
      setInboxMap(prev => ({ ...prev, [selectedUser.id]: updated }));
    } catch (e) { message.error('更新失敗'); }
  };

  const handleSend = async () => {
    if (!replyText.trim() || !selectedUser) return;
    setSending(true);
    try {
      const res = await linePost('/push', { to: selectedUser.id, text: replyText.trim() });
      if (res.ok) {
        message.success('訊息已送出 ✅');
        setReplyText('');
        const newMsg = {
          _id: 'local_' + Date.now(), direction: 'outbound',
          text: replyText.trim(), user_id: 'bot:mira',
          reply_to: selectedUser.id, created_at: new Date().toISOString(),
        };
        setSelectedUser(prev => ({ ...prev, messages: [...(prev.messages || []), newMsg] }));
        setTimeout(fetchData, 2000);
        // Auto mark in_progress when first human reply
        if (inboxRec && inboxRec.status === 'pending') {
          updateInboxRec({ status: 'in_progress' });
        }
      } else {
        message.error('發送失敗：' + (res.error || JSON.stringify(res)));
      }
    } catch (e) { message.error('連線失敗：' + e.message); }
    finally { setSending(false); }
  };

  const filteredContacts = channelTab === 'all'
    ? contacts
    : contacts.filter(c => c.channel === channelTab);

  const chatMessages = [...(selectedUser?.messages || [])].sort((a, b) => a.created_at.localeCompare(b.created_at));

  const tagMap = Object.fromEntries(tags.map(t => [t.id, t]));

  return (
    <Layout style={{ height: 'calc(100vh - 96px)', background: '#f0f2f5' }}>
      {/* ── Left: Contact List ─────────────────────────────── */}
      <Sider width={300} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
        {/* Inner flex wrapper — Sider doesn't support flex on itself reliably */}
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '10px 12px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Space>
              <Text strong style={{ fontSize: 14 }}>收件匣</Text>
              <Badge count={contacts.reduce((s, c) => s + (c.unread || 0), 0)} color="#06c755" />
            </Space>
            <Space size={4}>
              <Tooltip title="標籤管理">
                <Button size="small" icon={<SettingOutlined />} onClick={() => setTagDrawer(true)} type="text" />
              </Tooltip>
              <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={fetchData} type="text" />
            </Space>
          </div>
          {/* Channel filter */}
          <Segmented
            size="small"
            value={channelTab}
            onChange={setChannelTab}
            block
            options={[
              { label: '全部', value: 'all' },
              { label: <Space size={3}>{CHANNEL_ICON.line}<span>LINE</span></Space>, value: 'line' },
              { label: <Space size={3}>{CHANNEL_ICON.fb}<span>FB</span></Space>, value: 'fb' },
            ]}
          />
        </div>

        {/* Contact list */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : filteredContacts.length === 0 ? (
            <Empty description="尚無聯絡人" style={{ marginTop: 40 }} />
          ) : (
            filteredContacts.map(user => {
              const rec = inboxMap[user.id];
              const primaryTag = rec?.primary_tag_id ? tagMap[rec.primary_tag_id] : null;
              const displayName = rec?.display_name || user.id.slice(0, 12) + '…';
              const status = rec?.status || 'pending';
              return (
                <div key={user.id}
                  onClick={() => selectUser(user)}
                  style={{
                    padding: '10px 12px', cursor: 'pointer',
                    background: selectedUser?.id === user.id ? '#e6f7ff' : 'transparent',
                    borderLeft: selectedUser?.id === user.id ? '3px solid #06c755' : '3px solid transparent',
                    borderBottom: '1px solid #f5f5f5',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Badge count={user.unread > 0 ? user.unread : 0} color="#06c755" size="small">
                      <Avatar size={36} style={{ background: '#06c755', flexShrink: 0 }} icon={<UserOutlined />} />
                    </Badge>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space size={4}>
                          {CHANNEL_ICON[user.channel || 'line']}
                          <Text strong style={{ fontSize: 12 }}>{displayName}</Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 10, flexShrink: 0 }}>
                          {user.lastAt ? new Date(user.lastAt).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <Tag color={STATUS[status]?.color} style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                          {STATUS[status]?.label}
                        </Tag>
                        {primaryTag && <Tag color={primaryTag.color} style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{primaryTag.name}</Tag>}
                      </div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }} ellipsis>
                        {user.lastText || '(尚無訊息)'}
                      </Text>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        </div>{/* end inner flex wrapper */}
      </Sider>

      {/* ── Right: Chat + Controls ─────────────────────────── */}
      <Content style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedUser ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12 }}>
            <MessageOutlined style={{ fontSize: 48, color: '#ccc' }} />
            <Text type="secondary">點選左側聯絡人開始對話</Text>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Avatar style={{ background: '#06c755' }} icon={<UserOutlined />} />
                  <div>
                    <Space size={6}>
                      <Text strong style={{ fontSize: 13 }}>
                        {inboxRec?.display_name || selectedUser.id.slice(0, 16) + '…'}
                      </Text>
                      <Tooltip title="編輯名稱">
                        <EditOutlined style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}
                          onClick={() => { setNameInput(inboxRec?.display_name || ''); setNameEditModal(true); }} />
                      </Tooltip>
                    </Space>
                    <br />
                    <Space size={4} wrap>
                      {CHANNEL_ICON[selectedUser.channel || 'line']}
                      <Tag color={STATUS[inboxRec?.status || 'pending']?.color} style={{ fontSize: 11 }}>
                        {STATUS[inboxRec?.status || 'pending']?.label}
                      </Tag>
                      <Tag style={{ fontSize: 10 }}>{chatMessages.length} 則訊息</Tag>
                    </Space>
                  </div>
                </Space>
                <Space>
                  {/* Status change */}
                  <Select size="small" value={inboxRec?.status || 'pending'} style={{ width: 100 }}
                    onChange={v => updateInboxRec({ status: v })}>
                    {Object.entries(STATUS).map(([k, v]) => (
                      <Select.Option key={k} value={k}><Tag color={v.color}>{v.label}</Tag></Select.Option>
                    ))}
                  </Select>
                  {/* Intent tag selector */}
                  <Select
                    size="small" mode="multiple" placeholder="意圖標籤"
                    style={{ width: 180 }} maxTagCount={2}
                    value={inboxRec?.intent_tags || []}
                    onChange={ids => {
                      const primary = ids.length > 0 ? ids[0] : null;
                      updateInboxRec({ intent_tags: ids, primary_tag_id: primary });
                    }}
                  >
                    {tags.filter(t => t.is_active).map(t => (
                      <Select.Option key={t.id} value={t.id}><Tag color={t.color}>{t.name}</Tag></Select.Option>
                    ))}
                  </Select>
                  <Button size="small" icon={<ReloadOutlined />} onClick={fetchData} loading={refreshing} />
                </Space>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px', background: '#f0f2f5' }}>
              {chatMessages.map((msg, idx) => {
                const isIn = msg.direction === 'inbound';
                return (
                  <div key={msg._id || idx} style={{ display: 'flex', justifyContent: isIn ? 'flex-start' : 'flex-end', marginBottom: 12 }}>
                    {isIn && <Avatar size={32} style={{ background: '#06c755', flexShrink: 0, marginRight: 8, marginTop: 4 }} icon={<UserOutlined />} />}
                    <div style={{ maxWidth: '70%' }}>
                      <div style={{
                        background: isIn ? '#fff' : '#06c755', color: isIn ? '#333' : '#fff',
                        borderRadius: isIn ? '0 12px 12px 12px' : '12px 0 12px 12px',
                        padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {msg.text}
                      </div>
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 3, textAlign: isIn ? 'left' : 'right' }}>
                        {msg.created_at ? new Date(msg.created_at).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        {!isIn && <RobotOutlined style={{ marginLeft: 4 }} />}
                      </Text>
                    </div>
                    {!isIn && <Avatar size={32} style={{ background: '#1890ff', flexShrink: 0, marginLeft: 8, marginTop: 4 }} icon={<RobotOutlined />} />}
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Quick replies */}
            <div style={{ padding: '6px 16px', background: '#fafafa', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Text type="secondary" style={{ fontSize: 11, width: '100%' }}><BulbOutlined /> 快速回覆：</Text>
              {QUICK_REPLIES.slice(0, 3).map((qr, i) => (
                <Button key={i} size="small" style={{ fontSize: 11, height: 24 }} onClick={() => setReplyText(qr)}>
                  {qr.slice(0, 18)}…
                </Button>
              ))}
            </div>

            {/* Reply box */}
            <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid #f0f0f0' }}>
              <TextArea
                value={replyText} onChange={e => setReplyText(e.target.value)}
                placeholder="輸入回覆訊息... (Ctrl+Enter 送出)"
                autoSize={{ minRows: 2, maxRows: 5 }}
                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); handleSend(); } }}
                style={{ marginBottom: 8, fontSize: 13 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>透過 Mira LINE Bot 回覆 · Ctrl+Enter 快速送出</Text>
                <Button type="primary" icon={<SendOutlined />} loading={sending}
                  disabled={!replyText.trim()} onClick={handleSend}
                  style={{ background: '#06c755', border: 'none' }}>
                  送出
                </Button>
              </div>
            </div>
          </>
        )}
      </Content>

      {/* ── Tag Manager Drawer ──────────────────────────────── */}
      <TagManagerDrawer open={tagDrawer} onClose={() => setTagDrawer(false)} tags={tags} onTagsChange={setTags} />

      {/* ── Edit Name Modal ─────────────────────────────────── */}
      <Modal title="編輯客戶名稱" open={nameEditModal} onOk={async () => {
        await updateInboxRec({ display_name: nameInput });
        setNameEditModal(false);
        message.success('名稱已更新');
      }} onCancel={() => setNameEditModal(false)} okText="儲存" cancelText="取消">
        <Input value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="輸入客戶名稱" />
      </Modal>
    </Layout>
  );
}
